import React from 'react'
import { ThemeColor } from 'types/color'
import { CouponDetailType } from 'types/coupon'
import { displayPrice } from 'helpers'
import { checkInvalidCampaign } from 'helpers/dine-in'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import Button from 'components/base-ui/Button'
import BtnBack from 'components/base-ui/BtnBack'
import ModalConfirm from 'components/base-ui/ModalConfirm'
import { Fonts } from 'components/base-ui'
import QRCodeSection from './QRCodeSection'
import {
  Wrapper,
  Image,
  Title,
  Description,
  PointRequired,
  Period,
  BackWrapper,
  ConfirmInfo,
  RedeemInfo,
  RedeemButtonWrapper,
  Point,
} from './styled'
import colors from 'styles/colors'

interface CouponDetailProps {
  coupon: CouponDetailType
  couponQRCode?: string
  loading: boolean
  onClickBack: VoidFunction
  onRedemption: VoidFunction
  isRedeemable: boolean
  memberTotalPoints: number
  openModalRedeemPoints: boolean
  onCloseModalRedeemPoints: VoidFunction
  onClickRedeemPoints: VoidFunction
  openModalInvalid: boolean
  onCloseModalInvalid: VoidFunction
}

const CouponDetail = ({
  coupon,
  couponQRCode = '',
  onClickBack,
  loading,
  onRedemption,
  isRedeemable,
  memberTotalPoints,
  openModalRedeemPoints,
  onCloseModalRedeemPoints,
  onClickRedeemPoints,
  openModalInvalid,
  onCloseModalInvalid,
}: CouponDetailProps) => {
  const { thumbnailImageURL, srcSetImage, name, pointsRequire, startTime, endTime, description } = coupon
  const { t } = useTranslation()
  const period = `${dayjs(startTime).format('DD/MM/YY')} - ${dayjs(endTime).format('DD/MM/YY')}`
  const thumbnailImage = thumbnailImageURL ? thumbnailImageURL : '/assets/generic/promotions/coupon16x9.svg'
  const pointFormatted = t('dineIn.couponDetail.points', { points: displayPrice(pointsRequire) })
  const { buttonText, inActive } = checkInvalidCampaign(coupon, memberTotalPoints)

  return (
    <>
      <Wrapper>
        <BackWrapper>
          <BtnBack bold color={ThemeColor.primary} onClick={onClickBack} enableFastClick={false} />
        </BackWrapper>
        <Image src={thumbnailImage} srcSet={srcSetImage} alt="" />
        <div>
          <Title>{name}</Title>
          {isRedeemable ? (
            <>
              <PointRequired>{pointFormatted}</PointRequired>
              <Period>{t('dineIn.couponDetail.availablePeriod', { period })}</Period>
            </>
          ) : (
            <Period>{t('dineIn.couponDetail.couponUsage', { period })}</Period>
          )}
        </div>
        {isRedeemable && (
          <RedeemButtonWrapper>
            <Button
              onClick={onClickRedeemPoints}
              md
              disabled={inActive}
              style={inActive ? { color: colors.white } : {}}
            >
              {buttonText}
            </Button>
          </RedeemButtonWrapper>
        )}
        {!!couponQRCode && <QRCodeSection QRCodeImage={couponQRCode} serialNumber={coupon.serialNumber} />}
        <Description>{description}</Description>
      </Wrapper>

      <ModalConfirm
        isOpen={openModalRedeemPoints}
        onRequestClose={onCloseModalRedeemPoints}
        btnAccept={{
          content: t('dineIn.couponDetail.modalConfirm.confirm'),
          isProcessing: loading,
          onClick: onRedemption,
        }}
      >
        <Wrapper style={{ paddingBottom: 20 }}>
          <div style={{ textAlign: 'center' }}>
            <Fonts.H3>{t('dineIn.couponDetail.modalConfirm.title')}</Fonts.H3>
            <ConfirmInfo>
              {t('dineIn.couponDetail.modalConfirm.description1')} <strong>{pointFormatted}</strong>
              <br />
              {t('dineIn.couponDetail.modalConfirm.description2')}
            </ConfirmInfo>
          </div>
          <RedeemInfo>
            <ConfirmInfo>{name}</ConfirmInfo>
            <Point>
              <strong style={{ color: '#000000' }}>{pointFormatted}</strong>
            </Point>
          </RedeemInfo>
        </Wrapper>
      </ModalConfirm>

      <ModalConfirm
        isOpen={openModalInvalid}
        onRequestClose={onCloseModalInvalid}
        btnAccept={{
          content: t('dineIn.couponDetail.modalInvalid.ok'),
          isProcessing: false,
          onClick: onCloseModalInvalid,
        }}
      >
        <Wrapper style={{ padding: '20px 0' }}>
          <div style={{ textAlign: 'center' }}>
            <Fonts.H3>{t('dineIn.couponDetail.modalInvalid.title')}</Fonts.H3>
            <ConfirmInfo>{t('dineIn.couponDetail.modalInvalid.description')}</ConfirmInfo>
          </div>
        </Wrapper>
      </ModalConfirm>
    </>
  )
}

export default CouponDetail
